import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTenantBootstrap, useTenantOnboardingAccess } from '@tark/shared/hooks';
import { useAuth } from '@tark/shared/hooks';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input } from '@tark/shared/components';
import { Copy, KeyRound, MailPlus, ShieldCheck, Users } from 'lucide-react';
import CoachPageHeader from '../components/CoachPageHeader';

const MANAGER_ROLES = new Set(['OWNER', 'ADMIN', 'COACH']);

type LeadAccessDraft = {
  key: string;
  leadId: string;
  label: string;
  note: string;
  email: string;
  phone: string;
  autoCode: boolean;
};

function toIsoString(value: string): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

async function copyToClipboard(value: string) {
  if (typeof navigator !== 'undefined' && navigator.clipboard) {
    await navigator.clipboard.writeText(value);
  }
}

function buildJoinCodeUrl(code: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}/join-organization?code=${encodeURIComponent(code)}`;
}

function buildInvitationUrl(token: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}/join-organization?token=${encodeURIComponent(token)}`;
}

function formatDateTime(value?: string | null): string {
  if (!value) return 'No date';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString();
}

function parseLeadAccessDraft(searchParams: URLSearchParams): LeadAccessDraft | null {
  if (searchParams.get('source') !== 'lead') {
    return null;
  }

  const leadId = (searchParams.get('leadId') || '').trim();
  if (!leadId) {
    return null;
  }

  const label = (searchParams.get('label') || 'Lead family access').trim();
  const note = (searchParams.get('note') || '').trim();

  return {
    key: [
      leadId,
      label,
      searchParams.get('email') || '',
      searchParams.get('phone') || '',
      note,
      searchParams.get('autoCode') || '',
    ].join(':'),
    leadId,
    label,
    note,
    email: (searchParams.get('email') || '').trim(),
    phone: (searchParams.get('phone') || '').trim(),
    autoCode: searchParams.get('autoCode') === '1',
  };
}

export default function TenantAccessPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const { activeTenant } = useTenantBootstrap(Boolean(user));
  const isManager = MANAGER_ROLES.has(activeTenant?.roleCode || '');
  const isCoachStudio = activeTenant?.tenant?.tenantArchetype === 'COACHING';
  const {
    access,
    isLoading,
    createJoinCode,
    revokeJoinCode,
    createInvitation,
    revokeInvitation,
    refreshInvitation,
    deactivateMembership,
    reactivateMembership,
    isCreatingJoinCode,
    isCreatingInvitation,
    isRevokingJoinCode,
    isRevokingInvitation,
    isRefreshingInvitation,
    isDeactivatingMembership,
    isReactivatingMembership,
  } = useTenantOnboardingAccess(Boolean(user) && Boolean(activeTenant?.tenant?.id) && isManager);

  const [joinCodeLabel, setJoinCodeLabel] = useState('');
  const [joinCodeMaxRedemptions, setJoinCodeMaxRedemptions] = useState('');
  const [joinCodeExpiresAt, setJoinCodeExpiresAt] = useState('');
  const [joinCodeReusable, setJoinCodeReusable] = useState(true);

  const [inviteRoleCode, setInviteRoleCode] = useState<'GUARDIAN' | 'COACH' | 'STAFF' | 'ADMIN'>('GUARDIAN');
  const [inviteeEmail, setInviteeEmail] = useState('');
  const [inviteePhone, setInviteePhone] = useState('');
  const [inviteNote, setInviteNote] = useState('');
  const [inviteExpiresAt, setInviteExpiresAt] = useState('');

  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recentJoinCode, setRecentJoinCode] = useState<string | null>(null);
  const leadAutoCodeKey = useRef<string | null>(null);
  const leadAccessDraft = useMemo(() => parseLeadAccessDraft(searchParams), [searchParams]);

  const roleOptions = useMemo(() => (
    access?.canManageCoachAccess
      ? ['GUARDIAN', 'COACH', 'STAFF', 'ADMIN']
      : ['GUARDIAN']
  ), [access?.canManageCoachAccess]);

  useEffect(() => {
    if (!leadAccessDraft) {
      return;
    }

    setJoinCodeLabel(leadAccessDraft.label);
    setJoinCodeMaxRedemptions('1');
    setJoinCodeExpiresAt('');
    setJoinCodeReusable(false);
    setInviteRoleCode('GUARDIAN');
    setInviteeEmail(leadAccessDraft.email);
    setInviteePhone(leadAccessDraft.phone);
    setInviteNote(leadAccessDraft.note);
    setMessage('Lead details loaded. Share the one-use code or create the prefilled guardian invitation.');
    setError(null);
  }, [leadAccessDraft]);

  useEffect(() => {
    if (!leadAccessDraft?.autoCode || !activeTenant?.tenant?.id || !isManager) {
      return;
    }

    const key = `${activeTenant.tenant.id}:${leadAccessDraft.leadId}`;
    if (leadAutoCodeKey.current === key) {
      return;
    }

    const storageKey = `tark-lead-access-code:${key}`;
    if (typeof window !== 'undefined' && window.sessionStorage?.getItem(storageKey) === 'created') {
      return;
    }

    leadAutoCodeKey.current = key;
    setError(null);
    void createJoinCode({
      roleCode: 'GUARDIAN',
      codeLabel: leadAccessDraft.label,
      reusable: false,
      maxRedemptions: 1,
      expiresAt: null,
    })
      .then((created) => {
        if (typeof window !== 'undefined') {
          window.sessionStorage?.setItem(storageKey, 'created');
        }
        setRecentJoinCode(created.code);
        setMessage(`Created one-use family join code ${created.code}. The guardian invitation form is prefilled below.`);
      })
      .catch((createError: any) => {
        setError(createError?.response?.data?.message || createError?.message || 'Could not create the lead join code.');
      });
  }, [activeTenant?.tenant?.id, createJoinCode, isManager, leadAccessDraft]);

  const handleCreateJoinCode = async () => {
    setError(null);
    setMessage(null);
    try {
      const created = await createJoinCode({
        roleCode: 'GUARDIAN',
        codeLabel: joinCodeLabel || undefined,
        reusable: joinCodeReusable,
        maxRedemptions: joinCodeMaxRedemptions ? Number(joinCodeMaxRedemptions) : null,
        expiresAt: toIsoString(joinCodeExpiresAt),
      });
      setJoinCodeLabel('');
      setJoinCodeMaxRedemptions('');
      setJoinCodeExpiresAt('');
      setJoinCodeReusable(true);
      setRecentJoinCode(created.code);
      setMessage(`Created guardian join code ${created.code}.`);
    } catch (createError: any) {
      setError(createError?.response?.data?.message || createError?.message || 'Could not create join code.');
    }
  };

  const handleCreateInvitation = async () => {
    setError(null);
    setMessage(null);
    try {
      const created = await createInvitation({
        roleCode: inviteRoleCode,
        inviteeEmail: inviteeEmail || undefined,
        inviteePhone: inviteePhone || undefined,
        note: inviteNote || undefined,
        expiresAt: toIsoString(inviteExpiresAt),
      });
      setInviteeEmail('');
      setInviteePhone('');
      setInviteNote('');
      setInviteExpiresAt('');
      setInviteRoleCode('GUARDIAN');
      setMessage(`Created ${created.roleCode.toLowerCase()} invitation ${created.invitationToken.slice(0, 12)}...`);
    } catch (createError: any) {
      setError(createError?.response?.data?.message || createError?.message || 'Could not create invitation.');
    }
  };

  const handleCopy = async (value: string, label: string) => {
    try {
      await copyToClipboard(value);
      setMessage(`${label} copied.`);
      setError(null);
    } catch {
      setError(`Could not copy ${label.toLowerCase()}.`);
    }
  };

  const scrollToJoinCodes = () => {
    if (typeof document === 'undefined') {
      return;
    }
    document.getElementById('join-codes')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleRefreshInvitation = async (invitationId: number) => {
    setError(null);
    setMessage(null);
    try {
      const refreshed = await refreshInvitation({
        invitationId,
        payload: {},
      });
      setMessage(`Refreshed invitation for ${refreshed.inviteeEmail || refreshed.inviteePhone || refreshed.roleCode.toLowerCase()}.`);
    } catch (refreshError: any) {
      setError(refreshError?.response?.data?.message || refreshError?.message || 'Could not refresh invitation.');
    }
  };

  const handleMembershipAction = async (membershipId: number, nextAction: 'deactivate' | 'reactivate') => {
    setError(null);
    setMessage(null);
    try {
      if (nextAction === 'deactivate') {
        if (!window.confirm('Deactivate this organization membership? The user can be reactivated later.')) {
          return;
        }
        const member = await deactivateMembership(membershipId);
        setMessage(`Deactivated ${member.displayName || member.profileName || 'member'}.`);
        return;
      }

      const member = await reactivateMembership(membershipId);
      setMessage(`Reactivated ${member.displayName || member.profileName || 'member'}.`);
    } catch (membershipError: any) {
      setError(membershipError?.response?.data?.message || membershipError?.message || 'Could not update membership.');
    }
  };

  if (!activeTenant?.tenant?.id) {
    return (
      <Card variant="glass">
        <CardHeader>
          <CardTitle>Access</CardTitle>
          <CardDescription>Select Coach Studio first, then return here to manage join codes or invitations.</CardDescription>
        </CardHeader>
        <CardContent>
          <Link to="/profile">
            <Button>Go to Profile</Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  if (!isManager) {
    return (
      <Card variant="glass">
        <CardHeader>
          <CardTitle>Access</CardTitle>
          <CardDescription>
            This page is for coaches or admins who invite families and staff into {activeTenant.tenant.displayName}.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6 pb-20 md:pb-6">
      <CoachPageHeader
        title="Access"
        subtitle={
          isCoachStudio
            ? 'Create family join codes, invite staff, and manage who belongs to this Coach Studio.'
            : `Manage join codes, invitations, and membership for ${activeTenant.tenant.displayName}.`
        }
        meta={
          <span className="rounded-full border border-border bg-surface px-3 py-1 text-xs font-semibold text-text-muted">
            {activeTenant.tenant.displayName}
          </span>
        }
        action={
          <Button leftIcon={<KeyRound className="h-4 w-4" />} onClick={scrollToJoinCodes}>
            Create Join Code
          </Button>
        }
      />

      <Card id="setup" variant="glass" className="scroll-mt-24">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            {isCoachStudio ? 'Coach Studio Access' : 'Access'}
          </CardTitle>
          <CardDescription>
            {isCoachStudio
              ? 'Create family join codes, send direct invitations, and manage studio access.'
              : `Issue guardian join codes, send direct invitations, and keep onboarding inside ${activeTenant.tenant.displayName}.`}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="rounded-xl border border-border bg-surface-elevated/50 p-4">
            <p className="text-sm font-medium text-text">{isCoachStudio ? 'Active Studio' : 'Active Organization'}</p>
            <p className="mt-2 text-base font-semibold text-white">{activeTenant.tenant.displayName}</p>
            <p className="mt-1 text-sm text-text-muted">
              You are acting as {access?.activeRoleCode || activeTenant.roleCode}.
            </p>
          </div>
          {message ? (
            <div className="rounded-xl border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm text-green-200">
              {message}
            </div>
          ) : null}
          {error ? (
            <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
              {error}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {recentJoinCode ? (
        <Card variant="default" className="border-primary/20">
          <CardContent className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">Family join code ready</p>
              <p className="mt-2 text-2xl font-semibold tracking-tight text-text">{recentJoinCode}</p>
              <p className="mt-1 break-all text-sm text-text-muted">{buildJoinCodeUrl(recentJoinCode)}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => void handleCopy(recentJoinCode, 'Join code')}>
                Copy Code
              </Button>
              <Button variant="outline" onClick={() => void handleCopy(buildJoinCodeUrl(recentJoinCode), 'Join link')}>
                Copy Link
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-2">
        <Card variant="default">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="h-4 w-4 text-primary" />
              Members
            </CardTitle>
            <CardDescription>
              Everyone who currently belongs to {activeTenant.tenant.displayName}, including guardians with enrolled athletes.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {isLoading ? (
              <p className="text-sm text-text-muted">Loading members...</p>
            ) : access?.members?.length ? (
              access.members.map((member) => (
                <div key={member.membershipId} className="rounded-xl border border-border bg-surface-elevated/40 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-white">
                        {member.displayName || member.profileName || `User ${member.userId}`}
                      </p>
                      <p className="text-sm text-text-muted">
                        {member.roleCode} • {member.membershipStatus}
                        {member.currentUser ? ' • You' : ''}
                      </p>
                      {member.email || member.mobileNumber ? (
                        <p className="mt-1 text-xs text-text-subtle">
                          {member.email || member.mobileNumber}
                        </p>
                      ) : null}
                      <p className="mt-1 text-xs text-text-subtle">
                        Joined {formatDateTime(member.joinedAt)}
                      </p>
                      {member.activeAthleteCount > 0 ? (
                        <p className="mt-1 text-xs text-text-subtle">
                          {member.activeAthleteCount} athlete{member.activeAthleteCount === 1 ? '' : 's'} enrolled
                          {member.athleteNames.length ? `: ${member.athleteNames.join(', ')}` : ''}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex gap-2">
                      {member.canDeactivate ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => void handleMembershipAction(member.membershipId, 'deactivate')}
                          isLoading={isDeactivatingMembership}
                        >
                          Deactivate
                        </Button>
                      ) : null}
                      {member.canReactivate ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => void handleMembershipAction(member.membershipId, 'reactivate')}
                          isLoading={isReactivatingMembership}
                        >
                          Reactivate
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-text-muted">No members are linked to this organization yet.</p>
            )}
          </CardContent>
        </Card>

        <Card id="join-codes" variant="default" className="scroll-mt-24">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <KeyRound className="h-4 w-4 text-primary" />
              Family Join Codes
            </CardTitle>
            <CardDescription>
              Share one link with parents so they can join with their existing TARK account.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              label="Code Label"
              value={joinCodeLabel}
              onChange={(event) => setJoinCodeLabel(event.target.value)}
              placeholder="Spring juniors families"
            />
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Max Uses"
                value={joinCodeMaxRedemptions}
                onChange={(event) => setJoinCodeMaxRedemptions(event.target.value)}
                placeholder="Unlimited"
                inputMode="numeric"
              />
              <Input
                label="Expires At"
                type="datetime-local"
                value={joinCodeExpiresAt}
                onChange={(event) => setJoinCodeExpiresAt(event.target.value)}
              />
            </div>
            <label className="flex items-center gap-3 text-sm text-text">
              <input
                type="checkbox"
                checked={joinCodeReusable}
                onChange={(event) => setJoinCodeReusable(event.target.checked)}
                className="h-4 w-4 rounded border-border bg-surface"
              />
              Reusable code
            </label>
            <Button onClick={handleCreateJoinCode} isLoading={isCreatingJoinCode}>
              Create Family Join Code
            </Button>
          </CardContent>
        </Card>

        <Card id="invitations" variant="default" className="scroll-mt-24">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <MailPlus className="h-4 w-4 text-primary" />
              Direct Invitations
            </CardTitle>
            <CardDescription>
              Use targeted invitations when you want a specific coach, staff member, or guardian to claim access.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-text">Role</label>
              <select
                value={inviteRoleCode}
                onChange={(event) => setInviteRoleCode(event.target.value as 'GUARDIAN' | 'COACH' | 'STAFF' | 'ADMIN')}
                className="h-10 w-full rounded-lg border border-border bg-surface px-3.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              >
                {roleOptions.map((roleCode) => (
                  <option key={roleCode} value={roleCode}>
                    {roleCode}
                  </option>
                ))}
              </select>
            </div>
            <Input
              label="Invitee Email"
              value={inviteeEmail}
              onChange={(event) => setInviteeEmail(event.target.value)}
              placeholder="parent@example.com"
            />
            <Input
              label="Invitee Phone"
              value={inviteePhone}
              onChange={(event) => setInviteePhone(event.target.value)}
              placeholder="+1 555 555 5555"
            />
            <Input
              label="Notes"
              value={inviteNote}
              onChange={(event) => setInviteNote(event.target.value)}
              placeholder="Optional context for your records"
            />
            <Input
              label="Expires At"
              type="datetime-local"
              value={inviteExpiresAt}
              onChange={(event) => setInviteExpiresAt(event.target.value)}
            />
            <Button variant="secondary" onClick={handleCreateInvitation} isLoading={isCreatingInvitation}>
              Create Invitation
            </Button>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card variant="default">
          <CardHeader>
            <CardTitle className="text-base">Active Join Codes</CardTitle>
            <CardDescription>Family access links for this studio.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {isLoading ? (
              <p className="text-sm text-text-muted">Loading access data...</p>
            ) : access?.joinCodes?.length ? (
              access.joinCodes.map((joinCode) => (
                <div key={joinCode.id} className="rounded-xl border border-border bg-surface-elevated/40 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-white">{joinCode.code}</p>
                      <p className="text-sm text-text-muted">
                        {joinCode.codeLabel || 'Guardian join code'} • {joinCode.status}
                      </p>
                      <p className="mt-1 text-xs text-text-subtle">
                        {joinCode.redemptionCount} redemption{joinCode.redemptionCount === 1 ? '' : 's'}
                        {joinCode.maxRedemptions ? ` / ${joinCode.maxRedemptions}` : ''}
                      </p>
                      <p className="mt-2 break-all text-xs text-text-subtle">{buildJoinCodeUrl(joinCode.code)}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => void handleCopy(joinCode.code, 'Join code')}>
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => void handleCopy(buildJoinCodeUrl(joinCode.code), 'Join link')}>
                        Link
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => void revokeJoinCode(joinCode.id).catch((revokeError: any) => {
                          setError(revokeError?.response?.data?.message || revokeError?.message || 'Could not revoke join code.');
                        })}
                        isLoading={isRevokingJoinCode}
                      >
                        Revoke
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-xl border border-dashed border-border bg-surface-elevated/30 p-4">
                <p className="text-sm font-semibold text-text">No join codes yet</p>
                <p className="mt-1 text-sm text-text-muted">Create one above and share the link with the first family.</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card variant="default">
          <CardHeader>
            <CardTitle className="text-base">Invitations</CardTitle>
            <CardDescription>Direct access requests that can be accepted after sign-in.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {isLoading ? (
              <p className="text-sm text-text-muted">Loading access data...</p>
            ) : access?.invitations?.length ? (
              access.invitations.map((invitation) => (
                <div key={invitation.id} className="rounded-xl border border-border bg-surface-elevated/40 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-white">
                        {invitation.inviteeEmail || invitation.inviteePhone || invitation.invitationToken.slice(0, 12)}
                      </p>
                      <p className="text-sm text-text-muted">
                        {invitation.roleCode} • {invitation.invitationStatus}
                      </p>
                      <p className="mt-1 text-xs text-text-subtle">
                        Expires {formatDateTime(invitation.expiresAt)}
                      </p>
                      {invitation.note ? (
                        <p className="mt-1 text-xs text-text-subtle">{invitation.note}</p>
                      ) : null}
                      <p className="mt-2 break-all text-xs text-text-subtle">{buildInvitationUrl(invitation.invitationToken)}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void handleCopy(invitation.invitationToken, 'Invitation token')}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void handleCopy(buildInvitationUrl(invitation.invitationToken), 'Invitation link')}
                      >
                        Link
                      </Button>
                      {invitation.invitationStatus !== 'CLAIMED' ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => void handleRefreshInvitation(invitation.id)}
                          isLoading={isRefreshingInvitation}
                        >
                          Refresh
                        </Button>
                      ) : null}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => void revokeInvitation(invitation.id).catch((revokeError: any) => {
                          setError(revokeError?.response?.data?.message || revokeError?.message || 'Could not revoke invitation.');
                        })}
                        isLoading={isRevokingInvitation}
                      >
                        Revoke
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-text-muted">No invitations yet.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
